import React, { useState, useEffect } from 'react';
import { canManageFinance } from '@/lib/permissions';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Building2, MapPin, Percent, Search, GripVertical, UserCog, Wallet, FolderOpen, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AccountColorPicker } from '@/components/AccountColorPicker';

// Import cities from shared data file
import { INDONESIAN_CITIES } from '@/data/indonesianCities';

const Branches = ({ user }) => {
  const [branches, setBranches] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Groups management
  const [groups, setGroups] = useState([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    group_name: '',
    manager_id: '',
    status: 'ACTIVE',
    accounts: [],
    order_number: 9999
  });
  const [accountForm, setAccountForm] = useState({
    account_name: '',
    account_number: '',
    bank_name: '',
    balance: 0,
    color: ''
  });
  const [editingAccountId, setEditingAccountId] = useState(null);

  // DnD Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
    loadGroups();
  }, []);

  // Load groups from localStorage and existing branches
  const loadGroups = () => {
    const savedGroups = localStorage.getItem('branch_groups');
    if (savedGroups) {
      setGroups(JSON.parse(savedGroups));
    }
  };

  // Extract unique groups from branches and merge with saved groups
  useEffect(() => {
    if (branches.length > 0) {
      const existingGroups = branches
        .map(b => b.group_name)
        .filter(g => g && g.trim() !== '');
      
      const uniqueGroups = [...new Set([...groups, ...existingGroups])];
      setGroups(uniqueGroups);
      localStorage.setItem('branch_groups', JSON.stringify(uniqueGroups));
    }
  }, [branches]);

  const fetchData = async () => {
    try {
      const [branchesRes, managersRes] = await Promise.all([
        api.get('/branches'),
        api.get('/managers')
      ]);
      setBranches(branchesRes.data);
      setManagers(managersRes.data);
    } catch (error) {
      toast.error('Gagal memuat data cabang');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = fetchData;

  const handleDragEnd = async (event, status) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Get branches for this status - always filter by status for proper reordering
    const statusBranches = branches.filter(b => (status ? b.status === status : (b.status === 'ACTIVE' || !b.status)));
    const oldIndex = statusBranches.findIndex(b => b.id === active.id);
    const newIndex = statusBranches.findIndex(b => b.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder within status group
    const reordered = arrayMove(statusBranches, oldIndex, newIndex);
    
    // Update order numbers for this status group only
    const updates = reordered.map((b, idx) => ({
      id: b.id,
      order_number: idx + 1
    }));

    // Update UI immediately - only update branches in the current status group
    const updatedBranches = branches.map(b => {
      const update = updates.find(u => u.id === b.id);
      return update ? { ...b, order_number: update.order_number } : b;
    });
    setBranches(updatedBranches);

    // Send to backend
    try {
      await api.put('/branches/reorder', { items: updates, status });
      toast.success('Urutan berhasil diperbarui');
      // Refresh to ensure consistency
      await fetchBranches();
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Gagal memperbarui urutan: ' + (error.response?.data?.detail || error.message));
      // Revert on error
      fetchBranches();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.accounts.length === 0) {
      toast.error('Minimal 1 rekening harus ditambahkan');
      return;
    }

    if (submitting) return;
    setSubmitting(true);
    try {
      const branchData = {
        name: formData.name,
        location: formData.location,
        group_name: formData.group_name || null,
        manager_id: formData.manager_id || null,
        status: formData.status,
        accounts: formData.accounts,
        order_number: formData.order_number
      };

      if (editingBranch) {
        await api.put(`/branches/${editingBranch.id}`, branchData);
        toast.success('Cabang berhasil diperbarui');
      } else {
        await api.post('/branches', branchData);
        toast.success('Cabang berhasil ditambahkan');
      }

      setDialogOpen(false);
      fetchBranches();
      resetForm();
    } catch (error) {
      toast.error(editingBranch ? 'Gagal memperbarui cabang' : 'Gagal menambah cabang');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus cabang ini?')) {
      try {
        await api.delete(`/branches/${id}`);
        toast.success('Cabang berhasil dihapus');
        fetchBranches();
      } catch (error) {
        toast.error('Gagal menghapus cabang: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const openEditDialog = (branch) => {
    console.log('Opening edit dialog for branch:', branch.id);
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      location: branch.location || '',
      group_name: branch.group_name || '',
      manager_id: branch.manager_id || '',
      status: branch.status || 'ACTIVE',
      accounts: branch.accounts || [],
      order_number: branch.order_number || 9999
    });
    // Use setTimeout to ensure state is updated before opening dialog
    setTimeout(() => {
      setDialogOpen(true);
    }, 0);
  };

  const resetForm = () => {
    setFormData({ name: '', location: '', group_name: '', manager_id: '', status: 'ACTIVE', accounts: [], order_number: 9999 });
    setEditingBranch(null);
    setEditingAccountId(null);
    setAccountForm({ account_name: '', account_number: '', bank_name: '', balance: 0, color: '' });
  };

  // Group Management Functions
  const handleAddGroup = () => {
    if (!newGroupName.trim()) {
      toast.error('Nama grup tidak boleh kosong');
      return;
    }
    
    if (groups.includes(newGroupName.trim())) {
      toast.error('Grup sudah ada');
      return;
    }

    const updatedGroups = [...groups, newGroupName.trim()];
    setGroups(updatedGroups);
    localStorage.setItem('branch_groups', JSON.stringify(updatedGroups));
    setNewGroupName('');
    toast.success('Grup berhasil ditambahkan');
  };

  const handleDeleteGroup = (groupName) => {
    // Check if group is used by any branch
    const isUsed = branches.some(b => b.group_name === groupName);
    
    if (isUsed) {
      toast.error('Grup tidak dapat dihapus karena masih digunakan oleh cabang');
      return;
    }

    if (window.confirm(`Hapus grup "${groupName}"?`)) {
      const updatedGroups = groups.filter(g => g !== groupName);
      setGroups(updatedGroups);
      localStorage.setItem('branch_groups', JSON.stringify(updatedGroups));
      toast.success('Grup berhasil dihapus');
    }
  };

  const addAccount = () => {
    if (!accountForm.account_name) {
      toast.error('Nama rekening harus diisi');
      return;
    }
    // Editing an existing account in place (keep same id + balance)
    if (editingAccountId) {
      setFormData({
        ...formData,
        accounts: formData.accounts.map(acc =>
          acc.id === editingAccountId
            ? { ...acc, account_name: accountForm.account_name, account_number: accountForm.account_number, bank_name: accountForm.bank_name, color: accountForm.color || null }
            : acc
        )
      });
      setEditingAccountId(null);
      setAccountForm({ account_name: '', account_number: '', bank_name: '', balance: 0, color: '' });
      return;
    }
    const newAccount = {
      ...accountForm,
      color: accountForm.color || null,
      id: Date.now().toString(),
      balance: parseFloat(accountForm.balance) || 0
    };
    setFormData({ ...formData, accounts: [...formData.accounts, newAccount] });
    setAccountForm({ account_name: '', account_number: '', bank_name: '', balance: 0, color: '' });
  };

  const startEditAccount = (acc) => {
    setEditingAccountId(acc.id);
    setAccountForm({
      account_name: acc.account_name || '',
      account_number: acc.account_number || '',
      bank_name: acc.bank_name || '',
      balance: acc.balance || 0,
      color: acc.color || ''
    });
  };

  const cancelEditAccount = () => {
    setEditingAccountId(null);
    setAccountForm({ account_name: '', account_number: '', bank_name: '', balance: 0, color: '' });
  };

  const removeAccount = (id) => {
    const acc = formData.accounts.find(a => a.id === id);
    // GUARD: an account that holds a balance must not be deleted (it would break
    // the balance sheet and orphan its transaction history). Ask user to zero it first.
    if (acc && (parseFloat(acc.balance) || 0) !== 0) {
      toast.error(`Rekening "${acc.account_name}" masih memiliki saldo Rp.\u00a0${parseInt(acc.balance).toLocaleString('id-ID')}. Kosongkan saldo (transfer ke Rp 0) atau gunakan tombol Edit untuk ganti nama.`);
      return;
    }
    if (editingAccountId === id) cancelEditAccount();
    setFormData({
      ...formData,
      accounts: formData.accounts.filter(acc => acc.id !== id)
    });
  };

  const getBranchShareholders = (branchId) => {
    const shareholders = [];
    managers.forEach(manager => {
      const share = manager.branch_shares?.find(s => s.branch_id === branchId);
      if (share) {
        shareholders.push({
          manager_id: manager.id,
          manager_name: manager.name,
          percentage: share.percentage
        });
      }
    });
    // Sort by percentage descending (tertinggi di atas)
    return shareholders.sort((a, b) => b.percentage - a.percentage);
  };

  const canManageBranches = canManageFinance(user);

  const filteredBranches = branches.filter(branch =>
    (branch.name && branch.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (branch.location && branch.location.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Sort branches by order_number within each status group
  const sortedBranches = [...filteredBranches].sort((a, b) => (a.order_number || 9999) - (b.order_number || 9999));

  const SortableBranchRow = ({ branch, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: branch.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const shareholders = getBranchShareholders(branch.id);
    const manager = managers.find(m => m.id === branch.manager_id);

    return (
      <TableRow ref={setNodeRef} style={style} key={branch.id}>
        <TableCell>
          <div className="flex items-center gap-2">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
              <GripVertical size={16} className="text-slate-400" />
            </div>
            <span className="font-medium">{index + 1}</span>
          </div>
        </TableCell>
        <TableCell className="font-medium">{branch.name}</TableCell>
        <TableCell>
          {branch.group_name ? (
            <Badge variant="outline" className="text-xs">
              {branch.group_name}
            </Badge>
          ) : (
            <span className="text-slate-400 text-sm italic">-</span>
          )}
        </TableCell>
        <TableCell>
          {branch.location ? (
            <div className="flex items-center gap-1 text-sm text-slate-600">
              <MapPin size={14} />
              {branch.location}
            </div>
          ) : (
            <span className="text-slate-400 text-sm italic">-</span>
          )}
        </TableCell>
        <TableCell>
          {shareholders.length > 0 ? (
            <div className="space-y-1">
              {shareholders.map((sh, idx) => (
                <div key={idx} className="text-sm">
                  <span className="text-slate-700">{sh.manager_name}:</span>
                  <span className="text-teal-600 font-semibold ml-1">{sh.percentage}%</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-slate-400 italic text-sm">-</span>
          )}
        </TableCell>
        <TableCell>
          {manager ? (
            <div className="flex items-center gap-2">
              <UserCog size={16} className="text-teal-600" />
              <span className="text-sm font-medium">{manager.name}</span>
            </div>
          ) : (
            <span className="text-slate-400 italic text-sm">Belum ditentukan</span>
          )}
        </TableCell>
        <TableCell>
          {branch.accounts && branch.accounts.length > 0 ? (
            <div className="space-y-1">
              {branch.accounts.map((acc, idx) => {
                const balance = parseFloat(acc.balance) || 0;
                return (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <Wallet size={14} className="text-emerald-600" />
                    <div>
                      <span className="text-slate-700 font-medium px-1.5 rounded" style={acc.color ? { backgroundColor: acc.color } : undefined}>{acc.account_name}:</span>
                      <span className={`ml-1 font-semibold ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        Rp.&nbsp;{Math.abs(balance).toLocaleString('id-ID')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="text-slate-400 italic text-sm">-</span>
          )}
        </TableCell>
        <TableCell>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => openEditDialog(branch)}
              type="button"
            >
              <Edit size={14} />
            </Button>
            {canManageBranches && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => handleDelete(branch.id)}
                type="button"
              >
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>Manajemen Cabang</h1>
          <p className="text-slate-600 mt-1">Kelola cabang dan rekening perusahaan</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              type="text"
              placeholder="Cari nama atau lokasi cabang..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {canManageBranches && (
            <>
              <Button 
                variant="outline" 
                onClick={() => setGroupDialogOpen(true)}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <FolderOpen size={20} className="mr-2" />
                Kelola Grup
              </Button>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    <Plus size={20} className="mr-2" />
                    Tambah Cabang
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingBranch ? 'Edit Cabang' : 'Tambah Cabang Baru'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Nama Cabang *</Label>
                    <Input
                      id="name"
                      data-testid="branch-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Kota / Kabupaten</Label>
                    <SearchableSelect
                      value={formData.location}
                      onValueChange={(value) => setFormData({ ...formData, location: value })}
                      options={INDONESIAN_CITIES.map(city => ({ value: city, label: city }))}
                      placeholder="Pilih kota/kabupaten..."
                      searchPlaceholder="Ketik untuk mencari kota..."
                      emptyText="Kota tidak ditemukan"
                    />
                  </div>

                  <div>
                    <Label htmlFor="group_name">Grup / Kategori</Label>
                    <SearchableSelect
                      value={formData.group_name}
                      onValueChange={(value) => setFormData({ ...formData, group_name: value })}
                      options={[
                        { value: '', label: 'Tanpa grup' },
                        ...groups.sort().map(group => ({
                          value: group,
                          label: group
                        }))
                      ]}
                      placeholder="Pilih grup..."
                      searchPlaceholder="Ketik untuk mencari grup..."
                      emptyText="Grup tidak ditemukan"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Opsional - untuk mengelompokkan cabang. 
                      <button
                        type="button"
                        onClick={() => setGroupDialogOpen(true)}
                        className="text-blue-600 hover:underline ml-1"
                      >
                        Kelola grup
                      </button>
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="manager_id">Mitra Pengelola</Label>
                    <SearchableSelect
                      value={formData.manager_id}
                      onValueChange={(value) => setFormData({ ...formData, manager_id: value })}
                      options={[
                        { value: '', label: 'Belum ditentukan' },
                        ...managers.filter(m => m.status === 'ACTIVE' || !m.status).map(manager => ({
                          value: manager.id,
                          label: manager.name
                        }))
                      ]}
                      placeholder="Pilih mitra pengelola..."
                      searchPlaceholder="Ketik untuk mencari mitra..."
                      emptyText="Mitra tidak ditemukan"
                    />
                  </div>

                  <div>
                    <Label>Status *</Label>
                    <SearchableSelect
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                      options={[
                        { value: 'ACTIVE', label: 'Aktif' },
                        { value: 'INACTIVE', label: 'Non-Aktif' },
                        { value: 'BLACKLIST', label: 'Blacklist' }
                      ]}
                      placeholder="Pilih status..."
                      searchPlaceholder="Cari status..."
                    />
                  </div>

                  {/* Accounts Section */}
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold">Rekening Cabang (Minimal 1) *</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <Label className="text-sm">Nama Rekening *</Label>
                        <Input
                          value={accountForm.account_name}
                          onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })}
                          
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Nomor Rekening</Label>
                        <Input
                          value={accountForm.account_number}
                          onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })}
                          
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Nama Bank</Label>
                        <Input
                          value={accountForm.bank_name}
                          onChange={(e) => setAccountForm({ ...accountForm, bank_name: e.target.value })}
                          
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Saldo Awal</Label>
                        <Input
                          type="number"
                          value={accountForm.balance}
                          onChange={(e) => setAccountForm({ ...accountForm, balance: e.target.value })}
                          disabled={!!editingAccountId}
                        />
                        {editingAccountId && (
                          <p className="text-[11px] text-slate-500 mt-1">Saldo tidak dapat diubah saat mengedit nama rekening.</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="text-sm">Warna</Label>
                      <AccountColorPicker value={accountForm.color} onChange={(c) => setAccountForm({ ...accountForm, color: c })} />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button type="button" onClick={addAccount} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="add-account-btn">
                        <Plus size={16} className="mr-2" />
                        {editingAccountId ? 'Simpan Perubahan Rekening' : 'Tambah Rekening'}
                      </Button>
                      {editingAccountId && (
                        <Button type="button" variant="outline" onClick={cancelEditAccount} data-testid="cancel-edit-account-btn">
                          Batal
                        </Button>
                      )}
                    </div>

                    {formData.accounts.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <Label className="text-sm font-medium">Daftar Rekening:</Label>
                        {formData.accounts.map((acc) => (
                          <div key={acc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                            <div className="flex-1">
                              <p className="font-medium text-sm inline-block px-1.5 rounded" style={acc.color ? { backgroundColor: acc.color } : undefined}>{acc.account_name}</p>
                              <p className="text-xs text-slate-600">
                                {acc.bank_name && `${acc.bank_name} - `}
                                {acc.account_number || 'Tanpa nomor'} | 
                                Saldo: Rp.&nbsp;{parseInt(acc.balance).toLocaleString('id-ID')}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditAccount(acc)}
                              className="text-teal-600 hover:text-teal-800 hover:bg-teal-50"
                              data-testid={`edit-account-${acc.id}`}
                              title="Ubah nama rekening"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAccount(acc.id)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              data-testid={`remove-account-${acc.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Batal
                    </Button>
                    <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting} data-testid="branch-submit-btn">
                      {submitting ? 'Menyimpan...' : (editingBranch ? 'Perbarui' : 'Simpan')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Cabang Aktif */}
          {sortedBranches.filter(b => b.status === 'ACTIVE' || !b.status).length > 0 && (
            <Card className="border-white shadow-lg">
              <CardHeader className="bg-white border-b">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Badge variant="default" className="bg-green-600">Aktif</Badge>
                  Cabang Aktif
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'ACTIVE')}>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">No</TableHead>
                          <TableHead>Nama Cabang</TableHead>
                          <TableHead>Grup</TableHead>
                          <TableHead>Lokasi</TableHead>
                          <TableHead>Pemegang Saham</TableHead>
                          <TableHead>Mitra Pengelola</TableHead>
                          <TableHead>Saldo Rekening</TableHead>
                          {canManageBranches && <TableHead className="text-right">Aksi</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <SortableContext 
                        items={sortedBranches.filter(b => b.status === 'ACTIVE' || !b.status).map(b => b.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <TableBody>
                          {sortedBranches.filter(b => b.status === 'ACTIVE' || !b.status).map((branch, index) => (
                            <SortableBranchRow key={branch.id} branch={branch} index={index} />
                          ))}
                        </TableBody>
                      </SortableContext>
                    </Table>
                  </div>
                </DndContext>
              </CardContent>
            </Card>
          )}

          {/* Cabang Non-Aktif */}
          {sortedBranches.filter(b => b.status === 'INACTIVE').length > 0 && (
            <Card className="border-slate-300 shadow-lg">
              <CardHeader className="bg-slate-50 border-b border-slate-200">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Badge variant="secondary" className="bg-slate-400 text-white">Non-Aktif</Badge>
                  Cabang Non-Aktif
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'INACTIVE')}>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">No</TableHead>
                          <TableHead>Nama Cabang</TableHead>
                          <TableHead>Grup</TableHead>
                          <TableHead>Lokasi</TableHead>
                          <TableHead>Pemegang Saham</TableHead>
                          <TableHead>Mitra Pengelola</TableHead>
                          <TableHead>Saldo Rekening</TableHead>
                          {canManageBranches && <TableHead className="text-right">Aksi</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <SortableContext 
                        items={sortedBranches.filter(b => b.status === 'INACTIVE').map(b => b.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <TableBody>
                          {sortedBranches.filter(b => b.status === 'INACTIVE').map((branch, index) => (
                            <SortableBranchRow key={branch.id} branch={branch} index={index} />
                          ))}
                        </TableBody>
                      </SortableContext>
                    </Table>
                  </div>
                </DndContext>
              </CardContent>
            </Card>
          )}

          {/* Cabang Blacklist */}
          {sortedBranches.filter(b => b.status === 'BLACKLIST').length > 0 && (
            <Card className="border-red-300 shadow-lg">
              <CardHeader className="bg-red-50 border-b border-red-200">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Badge variant="destructive" className="bg-red-600">Blacklist</Badge>
                  Cabang Blacklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'BLACKLIST')}>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">No</TableHead>
                          <TableHead>Nama Cabang</TableHead>
                          <TableHead>Grup</TableHead>
                          <TableHead>Lokasi</TableHead>
                          <TableHead>Pemegang Saham</TableHead>
                          <TableHead>Mitra Pengelola</TableHead>
                          <TableHead>Saldo Rekening</TableHead>
                          {canManageBranches && <TableHead className="text-right">Aksi</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <SortableContext 
                        items={sortedBranches.filter(b => b.status === 'BLACKLIST').map(b => b.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <TableBody>
                          {sortedBranches.filter(b => b.status === 'BLACKLIST').map((branch, index) => (
                            <SortableBranchRow key={branch.id} branch={branch} index={index} />
                          ))}
                        </TableBody>
                      </SortableContext>
                    </Table>
                  </div>
                </DndContext>
              </CardContent>
            </Card>
          )}

          {sortedBranches.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center min-h-[300px] text-slate-500">
                <Building2 size={64} className="mb-4 opacity-20" />
                <p className="text-lg">Belum ada cabang yang ditambahkan</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Dialog Kelola Grup */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen size={20} className="text-blue-600" />
              Kelola Grup Cabang
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Form Tambah Grup */}
            <div className="flex gap-2">
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Nama grup baru..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddGroup();
                  }
                }}
              />
              <Button onClick={handleAddGroup} className="bg-blue-600 hover:bg-blue-700">
                <Plus size={16} className="mr-1" />
                Tambah
              </Button>
            </div>

            {/* Daftar Grup */}
            <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
              {groups.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <FolderOpen size={48} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Belum ada grup</p>
                  <p className="text-xs mt-1">Tambahkan grup untuk mengelompokkan cabang</p>
                </div>
              ) : (
                groups.sort().map((group, index) => {
                  const usageCount = branches.filter(b => b.group_name === group).length;
                  return (
                    <div key={index} className="flex items-center justify-between p-3 hover:bg-slate-50">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{group}</p>
                        <p className="text-xs text-slate-500">
                          {usageCount} cabang menggunakan grup ini
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteGroup(group)}
                        disabled={usageCount > 0}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>

            {groups.length > 0 && (
              <p className="text-xs text-slate-500 mt-2">
                💡 Tip: Grup yang sedang digunakan tidak dapat dihapus
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Branches;